import Controller from '@ember/controller';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { DataSet } from '@mov-uk/ember-shared/modifiers/chart';
import type { MapboxCustomCoordinate } from '@mov-uk/ember-shared/modifiers/mapbox';
import { task } from 'ember-concurrency';
import type { IntlService } from 'ember-intl';
import {
  type AlertType,
  type MutationUnlockBoxArgs,
  type ShipmentStateEnum,
  type TemperatureMeasurement,
} from 'graphql-models';
import config from 'walden-trace-app/config/environment';
import { SHIPMENT_STATE_TRANSLATIONS } from 'walden-trace-app/constants/shipment-states';
import unlockBoxMutation from 'walden-trace-app/graphql/shipments/unlock-box.graphql';
import type { AppShipmentsDetailModel } from 'walden-trace-app/routes/app/shipments/detail';
import type ApiService from 'walden-trace-app/services/api';
import type ModalService from 'walden-trace-app/services/modal';

export default class AppShipmentsDetailController extends Controller {
  declare model: AppShipmentsDetailModel;

  @service('api') declare apiService: ApiService;
  @service('intl') declare intlService: IntlService;
  @service('modal') declare modalService: ModalService;
  @service('router') declare routerService: RouterService;

  helpInfoModal = 'help-info';

  @tracked
  unlockBoxError = false;

  @tracked
  showUnlockBoxTutorial =
    config.alwaysShowUnlockTutorial ||
    window.localStorage.getItem('walden-trace-show-unlock-tutorial') === null;

  stateLabel = (state: string) => {
    return this.intlService.t(
      SHIPMENT_STATE_TRANSLATIONS[state.toUpperCase() as ShipmentStateEnum],
    );
  };

  get minMaxLines() {
    return [
      {
        label: this.intlService.t('pages.shipments.details.temperature.chart.excursionMaxRange', {
          temperature: this.model.shipment.excursionMaxTemperature || 0,
        }),
        value: this.model.shipment.excursionMaxTemperature || 0,
      },
      {
        label: this.intlService.t('pages.shipments.details.temperature.chart.excursionMinRange', {
          temperature: this.model.shipment.excursionMinTemperature || 0,
        }),
        value: this.model.shipment.excursionMinTemperature || 0,
      },
      {
        label: this.intlService.t('pages.shipments.details.temperature.chart.maxRange', {
          temperature: this.model.shipment.maxTemperature || 0,
        }),
        value: this.model.shipment.maxTemperature || 0,
      },
      {
        label: this.intlService.t('pages.shipments.details.temperature.chart.minRange', {
          temperature: this.model.shipment.minTemperature || 0,
        }),
        value: this.model.shipment.minTemperature || 0,
      },
    ];
  }

  get temperatureDataSet(): DataSet {
    if (!this.model.shipment.temperatureMeasurements) {
      return { label: '', data: [] };
    }

    return {
      label: this.intlService.t('pages.shipments.details.temperature.chart.temperature'),
      data: this.model.shipment.temperatureMeasurements
        .map((measurement: TemperatureMeasurement) => ({
          key: measurement.measuredAt,
          value: measurement.valueCelsius,
        }))
        .sort((a, b) => new Date(a.key).getTime() - new Date(b.key).getTime()),
    };
  }

  get temperatureDataSetMax(): number {
    return Math.ceil(
      Math.max(
        ...this.temperatureDataSet.data.map((measurement) => measurement.value),
        (this.model.shipment.maxTemperature || 0) + 1,
        (this.model.shipment.excursionMaxTemperature || 0) + 1,
      ),
    );
  }

  get temperatureDataSetMin(): number {
    return Math.floor(
      Math.min(
        ...this.temperatureDataSet.data.map((measurement) => measurement.value),
        (this.model.shipment.minTemperature || 0) - 1,
        (this.model.shipment.excursionMinTemperature || 0) - 1,
      ),
    );
  }

  get routeCoordinates() {
    if (!this.model.shipment.locationMeasurements) {
      return [];
    }
    return [...this.model.shipment.locationMeasurements]
      .sort((a, b) => new Date(a.measuredAt).getTime() - new Date(b.measuredAt).getTime())
      .map(({ latitude, longitude }) => [longitude, latitude]) as MapboxCustomCoordinate[];
  }

  get temperatureBreachError() {
    return this.model.shipment.alerts.some(
      (alert) => alert.type === ('TEMPERATURE_EXCEEDED' as AlertType),
    );
  }

  get shockDetectionError() {
    return this.model.shipment.alerts.some(
      (alert) => alert.type === ('SHOCK_DETECTED' as AlertType),
    );
  }

  get minimumMeasuredTemperature() {
    return Math.min(
      ...(this.model.shipment.temperatureMeasurements?.map(
        (measurement) => measurement.valueCelsius,
      ) || []),
    );
  }

  get maximumMeasuredTemperature() {
    return Math.max(
      ...(this.model.shipment.temperatureMeasurements?.map(
        (measurement) => measurement.valueCelsius,
      ) || []),
    );
  }

  get lastPicture() {
    return (
      this.model.shipment.pictures?.length &&
      this.model.shipment.pictures[this.model.shipment.pictures.length - 1]
    );
  }

  get pictures() {
    if (!this.model.shipment.pictures) {
      return [];
    }

    return this.model.shipment.pictures?.map((picture) => ({
      src: picture.bitmapUrl,
      alt: picture.createdAt.toString(),
      title: picture.createdAt.toString(),
      timestamp: new Date(picture.createdAt),
    }));
  }

  @action
  hideUnlockBoxTutorial() {
    this.showUnlockBoxTutorial = false;
    window.localStorage.setItem('walden-trace-show-unlock-tutorial', 'false');
  }

  @action
  showHelpInfoModal() {
    this.modalService.open(this.helpInfoModal);
  }

  unlockBox = task(async () => {
    try {
      this.unlockBoxError = false;
      if (!this.model.shipment?.box?.id) {
        this.unlockBoxError = true;
        console.error(`Box not found for shipment ${this.model.shipment?.id}`);
        return;
      }

      const result = await this.apiService.mutate<{ unlockBox: boolean }, MutationUnlockBoxArgs>({
        mutation: unlockBoxMutation,
        variables: {
          id: this.model.shipment.box.id,
        },
      });

      this.routerService.refresh();

      return result;
    } catch (error) {
      console.error(error);
      this.unlockBoxError = true;
      return error;
    }
  });
}
